if (!customElements.get('all-size-guides')) {
  class AllSizeGuides extends HTMLElement {
    constructor() {
      super();
      this.currentUnit = 'cm';
      this.SEPARATOR_CLASS = 'is-separator';
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('.all-size-guides__tab'));
      this.panels = Array.from(this.querySelectorAll('.all-size-guides__panel'));
      this.toggles = Array.from(this.querySelectorAll('.size-guides__toggle-btn'));
      this.tables = Array.from(this.querySelectorAll('table'));

      if (!this.tables.length) return;

      this.init();
    }

    init() {
      // Process each table to clean it up and set data-units
      this.tables.forEach((table) => {
        this.cleanupTable(table);
        this.processRows(table);
      });

      this.updateToggleVisibility();
      this.initSwiper();
      this.setupTabs();
      this.setupToggles();

      // Update striping initially for all tables
      this.tables.forEach((table) => this.updateRowStriping(table));
    }

    initSwiper() {
      const tabsContainer = this.querySelector('.all-size-guides__tabs.swiper');

      if (tabsContainer) {
        /* global Swiper */
        this.tabsSwiper = new Swiper(tabsContainer, {
          slidesPerView: 'auto',
          slidesPerGroup: 3,
          spaceBetween: 10,
          freeMode: true,
          watchSlidesProgress: true,
          navigation: {
            nextEl: this.querySelector('.all-size-guides__tab-next'),
            prevEl: this.querySelector('.all-size-guides__tab-prev'),
          },
        });
      }
    }

    updateActiveTab(index) {
      this.tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('is-active', isActive);
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive);

        if (isActive) {
          if (this.tabsSwiper) {
            this.tabsSwiper.slideTo(index);
          } else {
            tab.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }
        }
      });

      this.panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('is-active', isActive);
      });
    }

    cleanupTable(table) {
      table.removeAttribute('style');
      table.removeAttribute('border');
      table.removeAttribute('cellspacing');
      table.removeAttribute('cellpadding');
      table.classList.add('size-guides__table');

      this.applyColgroup(table);
      this.sanitizeElements(table);
      this.wrapTable(table);
    }

    applyColgroup(table) {
      const existingColgroup = table.querySelector('colgroup');
      if (existingColgroup) existingColgroup.remove();

      const firstRow = table.querySelector('tr');
      if (!firstRow) return;

      const colgroup = document.createElement('colgroup');
      Array.from(firstRow.cells).forEach((_, i) => {
        const col = document.createElement('col');
        col.style.width = i === 0 ? '130px' : '80px';
        colgroup.appendChild(col);
      });
      table.prepend(colgroup);
    }

    sanitizeElements(table) {
      table.querySelectorAll('*').forEach((el) => {
        if (el.tagName === 'COL' || el.tagName === 'COLGROUP') return;
        el.removeAttribute('style');
        el.removeAttribute('width');
        el.removeAttribute('height');

        if ((el.tagName === 'P' || el.tagName === 'SPAN') && !el.textContent.trim()) {
          el.remove();
        }
      });
    }

    wrapTable(table) {
      if (table.parentElement.classList.contains('size-guides__container')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'size-guides__container';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }

    processRows(table) {
      const rows = Array.from(table.querySelectorAll('tr'));
      let activeUnit = null;
      const tableUnits = new Set();

      rows.forEach((row) => {
        const firstCellText = row.cells[0]?.textContent.trim().toLowerCase() || '';

        if (firstCellText.includes('measurements in centimeters')) {
          activeUnit = 'cm';
          tableUnits.add('cm');
          row.classList.add(this.SEPARATOR_CLASS);
          row.style.display = 'none';

          return;
        } else if (firstCellText.includes('measurements in inches')) {
          activeUnit = 'in';
          tableUnits.add('in');
          row.classList.add(this.SEPARATOR_CLASS);
          row.style.display = 'none';

          return;
        }

        if (activeUnit) {
          row.setAttribute('data-unit', activeUnit);
          if (activeUnit === this.currentUnit) {
            row.style.display = '';
          } else {
            row.style.display = 'none';
          }
        }
      });

      // Store discovered units on the table element for later inspection
      table._foundUnits = tableUnits;
    }

    updateToggleVisibility() {
      // Collect all units found across every table
      const allUnits = new Set();
      this.tables.forEach((table) => {
        if (table._foundUnits) {
          table._foundUnits.forEach((u) => allUnits.add(u));
        }
      });

      const hasBothUnits = allUnits.has('cm') && allUnits.has('in');
      const toggleContainer = this.querySelector('.size-guides__toggle');

      if (hasBothUnits) {
        if (toggleContainer) toggleContainer.style.display = '';

        return;
      }

      // Hide the toggle switcher
      if (toggleContainer) toggleContainer.style.display = 'none';

      if (allUnits.size === 1) {
        this.showSingleUnit([...allUnits][0]);
      }
    }

    showSingleUnit(unit) {
      this.currentUnit = unit;
      this.tables.forEach((table) => {
        table.querySelectorAll('tr[data-unit]').forEach((row) => {
          row.style.display = '';
        });
        this.updateRowStriping(table);
      });
    }

    setupTabs() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          this.updateActiveTab(index);
        });
      });
    }

    setupToggles() {
      this.toggles.forEach((btn) => {
        btn.addEventListener('click', () => this.switchUnit(btn.dataset.unit));
      });
    }

    switchUnit(unit) {
      if (this.currentUnit === unit) return;
      this.currentUnit = unit;

      // Update toggle buttons
      this.toggles.forEach((btn) => {
        btn.classList.toggle('active', btn.textContent.toLowerCase().includes(unit));
      });

      // Update all tables
      this.tables.forEach((table) => {
        table.querySelectorAll('tr[data-unit]').forEach((row) => {
          if (row.getAttribute('data-unit') === unit) {
            row.style.display = '';
          } else {
            row.style.display = 'none';
          }
        });
        this.updateRowStriping(table);
      });
    }

    updateRowStriping(table) {
      let rowIndex = 1;
      table.querySelectorAll('tr').forEach((row) => {
        if (row.classList.contains(this.SEPARATOR_CLASS)) {
          rowIndex = 1;
        }

        if (row.style.display !== 'none' || row.classList.contains(this.SEPARATOR_CLASS)) {
          if (rowIndex % 2 !== 0) {
            row.classList.add('is-uneven');
          } else {
            row.classList.remove('is-uneven');
          }
          rowIndex++;
        } else {
          row.classList.remove('is-uneven');
        }
      });
    }
  }

  customElements.define('all-size-guides', AllSizeGuides);
}
